import { useState, useEffect, useCallback } from 'react';
import { featureFlagService, PublicFeatureFlags } from '../services/featureFlagService';

interface FeatureFlags extends PublicFeatureFlags {
  // Add other static flags if needed
  AI_CONTENT_CREATION: boolean;
  BRAND_KITS: boolean;
  MEDIA_LIBRARY: boolean;
  TEAM_COLLABORATION: boolean;
  SHOW_COMING_SOON_FEATURES: boolean;
  one_click_creator: boolean;
}

const DEFAULT_FLAGS: FeatureFlags = {
  publishing: false,
  scheduling: false,
  social_analytics: false,
  social_connections: false,
  one_click_creator: false,
  AI_CONTENT_CREATION: true,
  BRAND_KITS: true,
  MEDIA_LIBRARY: true,
  TEAM_COLLABORATION: true,
  SHOW_COMING_SOON_FEATURES: true,
};

// Global state for reactive updates
let globalFlags: FeatureFlags = DEFAULT_FLAGS;
let globalLoading = true;
let globalError: string | null = null;
let flagsPromise: Promise<void> | null = null;
let subscribers: Set<() => void> = new Set();

// Function to notify all subscribers of flag changes
const notifySubscribers = () => {
  subscribers.forEach(callback => callback());
};

// Function to invalidate and refetch flags
export const invalidateFeatureFlags = async () => {
  flagsPromise = null;
  globalLoading = true;
  notifySubscribers();
  
  try {
    const publicFlags = await featureFlagService.getPublicFlags();
    globalFlags = {
      ...DEFAULT_FLAGS,
      ...publicFlags,
    };
    globalError = null;
  } catch (err) {
    console.warn('Failed to load feature flags:', err);
    globalError = err instanceof Error ? err.message : 'Failed to load feature flags';
  } finally {
    globalLoading = false;
    notifySubscribers();
  }
};

export const useFeatureFlags = () => {
  const [flags, setFlags] = useState<FeatureFlags>(globalFlags);
  const [loading, setLoading] = useState(globalLoading);
  const [error, setError] = useState<string | null>(globalError);

  // Subscribe to global state changes
  const updateState = useCallback(() => {
    setFlags(globalFlags);
    setLoading(globalLoading);
    setError(globalError);
  }, []);

  useEffect(() => {
    // Subscribe to changes
    subscribers.add(updateState);
    
    // Initial load if needed
    const loadFlags = async () => {
      if (flagsPromise) {
        await flagsPromise;
        updateState();
        return;
      }

      if (globalLoading) {
        flagsPromise = (async () => {
          try {
            globalLoading = true;
            globalError = null;
            const publicFlags = await featureFlagService.getPublicFlags();
            
            globalFlags = {
              ...DEFAULT_FLAGS,
              ...publicFlags,
            };
          } catch (err) {
            console.warn('Failed to load feature flags:', err);
            globalError = err instanceof Error ? err.message : 'Failed to load feature flags';
          } finally {
            globalLoading = false;
            flagsPromise = null;
          }
        })();

        await flagsPromise;
        updateState();
      }
    };

    loadFlags();

    // Cleanup subscription
    return () => {
      subscribers.delete(updateState);
    };
  }, [updateState]);

  const refetch = useCallback(async () => {
    await invalidateFeatureFlags();
  }, []);

  return {
    flags,
    loading,
    error,
    refetch,
  };
};

export const useFeatureFlag = (flagName: keyof FeatureFlags) => {
  const { flags, loading, error, refetch } = useFeatureFlags();
  
  return {
    isEnabled: flags[flagName],
    loading,
    error,
    refetch,
  };
};

// Preload function to fetch flags early
export const preloadFeatureFlags = async () => {
  if (flagsPromise) return flagsPromise;
  
  flagsPromise = (async () => {
    try {
      globalLoading = true;
      globalError = null;
      const publicFlags = await featureFlagService.getPublicFlags();
      
      globalFlags = {
        ...DEFAULT_FLAGS,
        ...publicFlags,
      };
    } catch (err) {
      console.warn('Failed to preload feature flags:', err);
      globalError = err instanceof Error ? err.message : 'Failed to load feature flags';
    } finally {
      globalLoading = false;
      flagsPromise = null;
    }
  })();
  
  return flagsPromise;
};